import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import React from "react";
import { CameraMedia } from "./interfaces";
import { Link } from "react-router-dom";
import { GalleryItem } from "helpers/types";

// Day folder key from the backend: "YYYY-MM-DD" (folder 1) or "YYYY-MM-DD_N" (folder N >= 2,
// the Nth SD card seen that day). A trailing ISO time part is tolerated.
export const parseCaptureFolderKey = (key: string) => {
    const [datePart, folderPart] = key.split('T')[0].split('_');
    const folder = folderPart ? (parseInt(folderPart, 10) || 1) : 1;
    return { date: datePart, folder };
}

// Label for a day folder key: "Tue Sep 15 2026", or "Tue Sep 15 2026 (2)" for a later folder.
export const getDisplayDate = (dt: string) => {
    const { date, folder } = parseCaptureFolderKey(dt);
    const label = new Date(date.replace(/-/g, "/")).toDateString();
    return folder > 1 ? `${label} (${folder})` : label;
}

export const getLocaleDateString = (dt: string) => {
    //console.log(navigator.language)
    return new Date(dt).toLocaleDateString(navigator.language,{
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    });
}

/**
 * @param dateString The date string to convert. Expects a format 
 * like "2024-10-08T06:27:00" or any format the built in JavaScript 
 * Date function supports
 * @return A formatted date string in a format of 2024-10-08-06-27
 */
export const formatDate_y_m_d_h_m = (dateString: string) => {
    try {
        const date = new Date(dateString);

        // Pad single-digit numbers with a leading zero
        const pad = (num: number) => {
            return num.toString().padStart(2, '0');
        }

        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1); // getMonth() is 0-indexed
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());

        return `${year}-${month}-${day}-${hours}-${minutes}`;
    } catch (error) {
        console.error("Failed to parse date string:", error);
        return "";
    }
}

export const loading = () => {
    return (
        <div className="d-flex justify-content-center">
            <Spinner className="text-primary m-2"/>
        </div>
    );
}

export const roundOff = (n: number, decimalPlaces = 3) => {
    if (!n) {
        return 0;
    }
    return Number(n.toFixed(decimalPlaces));
}

export const calculateAbsoluteROIValues = (roiRect: any, imageWidth: number, imageHeight: number) => {
    return {
        roiX: Math.floor(roiRect.relative_x * imageWidth),
        roiY: Math.floor(roiRect.relative_y * imageHeight),
        roiWidth: Math.floor(roiRect.relative_width * imageWidth),
        roiHeight: Math.floor(roiRect.relative_height * imageHeight)
    }
}

export const calculateLineWidth = (multiplier: number) => {
    return Math.ceil(multiplier * 0.005);
}

export const roiColor = '#16b4c2';


export const getTimeIn12h = (date: string) => {
    return new Date(date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
}

export const getWeekDay = (date: string) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
    const day = new Date(date).getDay()
    return days[day];
}

// Matches only machine-generated video names carrying the 8-char uniqueness hash:
// 99099_20250724_0600_sd_00001_a1b2c3d4.mp4 - renamed videos can never match.
const VIDEO_HASH_NAME_PATTERN = /^(\d+_\d{8}_\d{4}_sd_\d+)_[0-9a-f]{8}(\.\w+)$/i;

export const getVideoDisplayBaseName = (filename: string) => {
    // strip the mac prefix (everything through the first underscore)...
    const firstUnderscoreIndex = filename.indexOf('_');
    const name = firstUnderscoreIndex > -1 ? filename.substring(firstUnderscoreIndex + 1) : filename;
    // ...then hide the uniqueness hash on machine-generated names
    return name.replace(VIDEO_HASH_NAME_PATTERN, '$1$2');
};

export const addCounterToVideoName = (baseName: string, counter: number) => {
    const dotIndex = baseName.lastIndexOf('.');
    if (dotIndex === -1) return `${baseName} (${counter})`;
    return `${baseName.substring(0, dotIndex)} (${counter})${baseName.substring(dotIndex)}`;
};

export const getImageFilename = (image: CameraMedia) => {
    let filename = image.filename;
    if (image.media_type === 'VIDEO') {
        // Videos download under their displayed name: display_name carries the
        // (2)/(3) duplicate counter computed by the Videos tab; the fallback
        // still hides the mac prefix and the uniqueness hash.
        if (image.display_name) {
            return image.display_name;
        }
        return getVideoDisplayBaseName(filename);
    }
    if (image.hd_file_path !== null) {
        filename = filename.replace('sd', 'hd');
    }
    return filename;
};

export const CustomLink = ({id, className, children, onClick, title, to, ...rest}: any) => (
    <OverlayTrigger overlay={<Tooltip id={id}>{title}</Tooltip>}>
        <Link to={to} className={className} onClick={onClick} target={rest["target"]} download={rest["download"]}>{children}</Link>
    </OverlayTrigger>
)

export const getMediaFileDisplayNameDependingOnContext = (image: GalleryItem, page_name: string) => {
    if (page_name === 'Video') {
        if (image.display_name) {
            return image.display_name;
        }
        return getVideoDisplayBaseName(image.filename);
    }
    return '# ' + image.sequence;
}
